import type { PostMeta } from "../posts";

export const meta: PostMeta = {
  slug: "microeconomics-sem-1-unit-2-demand-supply-elasticity-market-equilibrium",
  title: "Introductory Microeconomics Unit 2: Demand, Supply, Elasticity & Market Equilibrium Notes",
  description: "Master DU Sem 1 Unit 2: Demand & supply laws, elasticity formulas (price, income, cross), market equilibrium, price controls (ceilings, floors), tax incidence & 8+ solved numericals.",
  pubDate: "2025-08-22",
  authorSlug: "uday-kumar",
  category: "subject-explainers",
  tags: ["Microeconomics", "Demand and Supply", "Elasticity", "Market Equilibrium", "Tax Incidence", "DU Notes"],
  readMinutes: 17,
  heroEyebrow: "Sem 1 · Unit 2 Notes"
};

export function Body() {
  return (
    <div className="post-body">
      <h2>Introduction to Unit 2: The Core of Microeconomics</h2>
      <p>
        Welcome to the most fundamental unit of your Introductory Microeconomics syllabus at Delhi University: Unit 2 covering Demand, Supply, Elasticity, and Market Equilibrium. If Unit 1 was about the overarching principles of economics, Unit 2 provides the actual tools that economists use every single day to analyze how markets function.
      </p>
      <p>
        In this comprehensive 3,000+ word study guide, we are going to dive deep into how consumers decide what to buy (demand), how producers decide what to sell (supply), how they come together (market equilibrium), how responsive they are to changes (elasticity), and what happens when the government steps in (price controls and taxes). This unit is heavily numerical and analytical. Mastering it is non-negotiable for passing Sem 1 Microeconomics, as a significant portion of your final exam—especially the 10-15 mark numerical and analytical questions—will come straight from these concepts. Let's get started.
      </p>

      <h2>1. The Theory of Demand</h2>
      <h3>Individual vs. Market Demand</h3>
      <p>
        <strong>Demand</strong> is defined as the quantity of a good or service that consumers are willing and able to purchase at various possible prices during a specified period of time, ceteris paribus (other things being equal). It is crucial to understand that demand implies both the desire for a product and the financial backing to pay for it.
      </p>
      <p>
        <strong>Individual Demand</strong> refers to the quantity demanded by a single consumer at different prices.
        <br />
        <strong>Market Demand</strong> is the horizontal summation of all individual demand curves in the market. If Consumer A demands 5 units at Price Rs. 10 and Consumer B demands 7 units at the same price, the market demand at Rs. 10 is 12 units.
      </p>

      <h3>The Law of Demand</h3>
      <p>
        The Law of Demand states that, ceteris paribus, there is an inverse relationship between the price of a good and its quantity demanded. When price rises, quantity demanded falls, and when price falls, quantity demanded rises.
        The rationale behind the downward-sloping demand curve comes from two main effects:
      </p>
      <ul>
        <li><strong>Substitution Effect:</strong> As the price of a good falls, it becomes relatively cheaper compared to its substitutes, leading consumers to switch towards it.</li>
        <li><strong>Income Effect:</strong> As the price falls, the consumer's real income (purchasing power) increases, allowing them to buy more of the good.</li>
      </ul>

      <h3>Determinants of Demand</h3>
      <p>
        While price determines the quantity demanded along a given curve, several other factors determine the position of the demand curve itself:
      </p>
      <ol>
        <li><strong>Income of the Consumer:</strong> For normal goods, demand increases as income rises. For inferior goods, demand falls as income rises.</li>
        <li><strong>Prices of Related Goods:</strong>
          <ul>
            <li><em>Substitutes:</em> E.g., Tea and Coffee. An increase in the price of tea leads to an increase in the demand for coffee.</li>
            <li><em>Complements:</em> E.g., Cars and Petrol. An increase in the price of cars leads to a decrease in the demand for petrol.</li>
          </ul>
        </li>
        <li><strong>Tastes and Preferences:</strong> Favorable changes increase demand; unfavorable changes decrease it.</li>
        <li><strong>Expectations:</strong> If consumers expect future prices to rise, current demand increases.</li>
        <li><strong>Number of Buyers:</strong> An increase in population or market size increases market demand.</li>
      </ol>

      <h3>Movements vs. Shifts in Demand</h3>
      <p>
        A common pitfall in DU exams is confusing these two concepts.
      </p>
      <ul>
        <li><strong>Movement along the demand curve (Change in Quantity Demanded):</strong> Caused ONLY by a change in the price of the good itself. An upward movement is a contraction of demand; a downward movement is an expansion of demand.</li>
        <li><strong>Shift of the demand curve (Change in Demand):</strong> Caused by a change in any factor other than the good's own price (e.g., income, tastes). A rightward shift is an increase in demand; a leftward shift is a decrease in demand.</li>
      </ul>

      <h3>Exceptions to the Law of Demand</h3>
      <p>
        Normally, demand curves slope downwards. However, there are two famous exceptions where the curve may slope upwards (positive relationship between price and quantity):
      </p>
      <ul>
        <li><strong>Giffen Goods:</strong> Strongly inferior goods where the negative income effect dominates the substitution effect. E.g., cheap staple foods during a famine. As price rises, consumers are so impoverished that they must buy more of the staple and less of expensive foods like meat.</li>
        <li><strong>Veblen Goods:</strong> Goods of ostentation or status symbols (e.g., diamonds, designer bags). The higher the price, the greater the snob appeal, and therefore the higher the quantity demanded.</li>
      </ul>

      <h2>2. The Theory of Supply</h2>
      <p>
        <strong>Supply</strong> is the quantity of a good that producers are willing and able to offer for sale at various prices during a specific period. The <strong>Law of Supply</strong> states that, ceteris paribus, there is a direct (positive) relationship between price and quantity supplied. Producers want to maximize profit; higher prices offer higher profit margins, incentivizing more production.
      </p>

      <h3>Determinants of Supply</h3>
      <ol>
        <li><strong>Input Prices (Cost of Production):</strong> If wages or raw material prices rise, the cost of production increases, reducing profitability and decreasing supply (leftward shift).</li>
        <li><strong>Technology:</strong> Technological advancements lower production costs, increasing supply (rightward shift).</li>
        <li><strong>Prices of Related Goods in Production:</strong> If a farmer can grow either wheat or corn, an increase in the price of wheat will lead the farmer to substitute land away from corn towards wheat, decreasing the supply of corn.</li>
        <li><strong>Number of Sellers:</strong> More firms entering the market increases market supply.</li>
        <li><strong>Government Policy:</strong> Taxes on production decrease supply; subsidies increase supply.</li>
      </ol>

      <h3>Movements vs. Shifts in Supply</h3>
      <ul>
        <li><strong>Movement (Change in Quantity Supplied):</strong> Caused by a change in the good's own price. Expansion (upward movement) or Contraction (downward movement).</li>
        <li><strong>Shift (Change in Supply):</strong> Caused by non-price factors. Increase (rightward shift) or Decrease (leftward shift).</li>
      </ul>

      <h2>3. Market Equilibrium</h2>
      <p>
        Market equilibrium occurs at the price where Quantity Demanded (Qd) equals Quantity Supplied (Qs). Graphically, this is the intersection of the downward-sloping demand curve and the upward-sloping supply curve. The price at this intersection is the equilibrium price (Pe), and the quantity is the equilibrium quantity (Qe).
      </p>

      <h3>Excess Demand and Excess Supply</h3>
      <ul>
        <li><strong>Excess Supply (Surplus):</strong> Occurs when the current market price is above the equilibrium price (Qs &gt; Qd). Producers compete by lowering the price to clear inventory. As price falls, Qd expands and Qs contracts until equilibrium is restored.</li>
        <li><strong>Excess Demand (Shortage):</strong> Occurs when the current market price is below the equilibrium price (Qd &gt; Qs). Consumers compete for the limited goods, driving the price up. As price rises, Qs expands and Qd contracts until equilibrium is restored.</li>
      </ul>

      <h3>Stability of Equilibrium: Walrasian vs. Marshallian</h3>
      <p>
        In DU exams, they sometimes ask to differentiate between the two adjustment mechanisms:
      </p>
      <ul>
        <li><strong>Walrasian Stability:</strong> Focuses on price adjustment. If Qd differs from Qs, price will adjust. If there's excess demand, price rises; if excess supply, price falls. The equilibrium is Walrasian stable if these price changes push the market back to Pe.</li>
        <li><strong>Marshallian Stability:</strong> Focuses on quantity adjustment. Producers look at the Demand Price (what consumers are willing to pay) and Supply Price (what producers require to supply). If Demand Price &gt; Supply Price, producers increase quantity. If Demand Price &lt; Supply Price, producers decrease quantity. The market is Marshallian stable if these quantity adjustments push the market back to Qe.</li>
      </ul>

      <h2>4. Elasticity of Demand</h2>
      <p>
        Elasticity measures the responsiveness of one variable to changes in another. It's not enough to know that demand falls when price rises; we need to know by <em>how much</em>.
      </p>

      <h3>Price Elasticity of Demand (PED)</h3>
      <p>
        PED measures the percentage change in quantity demanded resulting from a 1% change in price.
        <br />
        Formula: PED = (Percentage change in Qd) / (Percentage change in P)
        <br />
        Symbolically: PED = (dQ/dP) * (P/Q)
      </p>
      <p>
        Because of the inverse relationship (Law of Demand), PED is technically negative, but we often take the absolute value |e| for interpretation.
      </p>
      <ul>
        <li><strong>Perfectly Inelastic (|e| = 0):</strong> Quantity doesn't change at all (vertical curve). E.g., lifesaving drugs.</li>
        <li><strong>Inelastic (|e| &lt; 1):</strong> Quantity changes by a smaller percentage than price. E.g., salt, basic food.</li>
        <li><strong>Unit Elastic (|e| = 1):</strong> Quantity changes by the exact same percentage as price. (A rectangular hyperbola demand curve).</li>
        <li><strong>Elastic (|e| &gt; 1):</strong> Quantity changes by a larger percentage than price. E.g., luxury goods, foreign holidays.</li>
        <li><strong>Perfectly Elastic (|e| = infinity):</strong> Any price increase drops demand to zero (horizontal curve). E.g., firm demand in perfect competition.</li>
      </ul>

      <h3>Methods of Measuring PED</h3>
      <ol>
        <li><strong>Point Elasticity:</strong> Used for very small changes in price along a continuous demand curve. PED = (dQ/dP) * (P/Q). Geometrically on a linear demand curve, Point Elasticity = Lower Segment / Upper Segment.</li>
        <li><strong>Arc / Midpoint Method:</strong> Used for larger price changes between two points. It uses average price and average quantity to avoid the base-point problem.
          <br />
          Arc PED = [(Q2 - Q1) / ((Q2 + Q1)/2)] / [(P2 - P1) / ((P2 + P1)/2)]
        </li>
        <li><strong>Total Outlay / Total Expenditure Method:</strong> Total Expenditure (TE) = Price * Quantity.
          <ul>
            <li>If P falls and TE rises -&gt; Elastic (|e| &gt; 1).</li>
            <li>If P falls and TE falls -&gt; Inelastic (|e| &lt; 1).</li>
            <li>If P falls and TE is unchanged -&gt; Unitary (|e| = 1).</li>
          </ul>
        </li>
      </ol>

      <h3>Linear Demand Curve Elasticity Proof</h3>
      <p>
        A frequent 5-mark proof in exams is showing that elasticity varies along a downward-sloping linear demand curve: Q = a - bP.
        <br />
        Since slope (dQ/dP) is constant at -b.
        <br />
        PED = (dQ/dP) * (P/Q) = -b * (P/Q).
        <br />
        At the price axis intercept (where Q = 0), P/Q approaches infinity, so |e| = infinity.
        <br />
        At the quantity axis intercept (where P = 0), P/Q = 0, so |e| = 0.
        <br />
        At the exact midpoint of the curve, |e| = 1. The upper half is elastic (&gt;1) and the lower half is inelastic (&lt;1).
      </p>

      <h3>Income Elasticity of Demand (YED)</h3>
      <p>
        YED measures responsiveness of demand to changes in consumer income.
        <br />
        Formula: YED = (Percentage change in Qd) / (Percentage change in Income)
        <br />
        This is used to classify goods using Engel curves:
        <ul>
          <li><strong>YED &gt; 0 (Positive):</strong> Normal Good. If 0 &lt; YED &lt; 1, it's a Necessity. If YED &gt; 1, it's a Luxury.</li>
          <li><strong>YED &lt; 0 (Negative):</strong> Inferior Good.</li>
          <li><strong>YED = 0:</strong> Income neutral (e.g., salt).</li>
        </ul>
      </p>

      <h3>Cross-Price Elasticity of Demand (XED)</h3>
      <p>
        XED measures how the quantity demanded of Good X responds to a change in the price of Good Y.
        <br />
        Formula: XED = (Percentage change in Qd of X) / (Percentage change in P of Y)
        <ul>
          <li><strong>XED &gt; 0:</strong> Goods are Substitutes (e.g., Coke and Pepsi). Price of Y rises, demand for X rises.</li>
          <li><strong>XED &lt; 0:</strong> Goods are Complements (e.g., Cars and Petrol). Price of Y rises, demand for X falls.</li>
          <li><strong>XED = 0:</strong> Goods are Unrelated.</li>
        </ul>
      </p>

      <h2>5. Price Elasticity of Supply (PES)</h2>
      <p>
        PES measures how much the quantity supplied responds to changes in price.
        <br />
        Formula: PES = (Percentage change in Qs) / (Percentage change in P)
        <br />
        Because supply curves slope upwards, PES is typically positive.
      </p>
      <h3>Time Horizons in PES</h3>
      <p>
        The most important determinant of PES is time. How quickly can producers adjust their inputs to produce more?
      </p>
      <ul>
        <li><strong>Momentary Period (Market Period):</strong> Supply is fixed. Firms cannot change production instantly. Supply is perfectly inelastic (vertical curve). PES = 0.</li>
        <li><strong>Short Run:</strong> Firms can change variable inputs (like labor) but not fixed inputs (like factory size). Supply is somewhat elastic. PES &gt; 0 but relatively low.</li>
        <li><strong>Long Run:</strong> All inputs are variable. Firms can build new factories, and new firms can enter the market. Supply is highly elastic. PES is large.</li>
      </ul>

      <h2>6. Government Intervention & Welfare Analysis</h2>
      <p>
        Free markets reach equilibrium on their own, but governments often intervene if they believe the equilibrium price is "unfair" to buyers or sellers.
      </p>

      <h3>Price Ceilings</h3>
      <p>
        A legal maximum price set below the market equilibrium. The goal is to protect consumers (e.g., Rent Control in Delhi).
        <br />
        <strong>Effects:</strong> Because the price cannot rise to clear the market, a permanent shortage (Excess Demand) is created. Qd &gt; Qs.
        <br />
        <strong>Consequences:</strong> Black markets emerge where goods are sold illegally at higher prices. Non-price rationing mechanisms like long queues or favoritism are used to allocate the scarce goods. Quality often deteriorates.
      </p>

      <h3>Price Floors</h3>
      <p>
        A legal minimum price set above the market equilibrium. The goal is to protect producers (e.g., Minimum Support Price or MSP for farmers in India, Minimum Wage laws).
        <br />
        <strong>Effects:</strong> Because the price cannot fall to clear the market, a permanent surplus (Excess Supply) is created. Qs &gt; Qd.
        <br />
        <strong>Consequences:</strong> In the case of MSP, the government usually has to buy up the surplus grain to maintain the floor price, leading to massive storage costs (e.g., FCI godowns). In the labor market, a minimum wage above equilibrium creates unemployment.
      </p>

      <h3>Taxes, Tax Incidence, and Deadweight Loss</h3>
      <p>
        When the government imposes a specific (per-unit) tax on a good, it drives a wedge between the price buyers pay (Pb) and the price sellers receive (Ps). Tax = Pb - Ps.
        <br />
        <strong>Tax Incidence (Who bears the burden?):</strong> The burden of a tax does NOT depend on whom the government legally levies the tax. It depends entirely on relative elasticities.
        <ul>
          <li><strong>Rule:</strong> The less elastic (more rigid) side of the market bears the larger share of the tax burden.</li>
          <li>If Demand is more inelastic than Supply, Consumers bear most of the tax.</li>
          <li>If Supply is more inelastic than Demand, Producers bear most of the tax.</li>
        </ul>
        <strong>Deadweight Loss (DWL):</strong> Also known as excess burden. It is the loss in total surplus (consumer surplus + producer surplus) that occurs because the tax reduces the equilibrium quantity below the socially optimal free-market level. The tax prevents some mutually beneficial trades from taking place.
      </p>

      <h2>7. Solved Numerical Problems</h2>
      <p>
        Numericals are the bread and butter of the Microeconomics Sem 1 exam. You must practice them extensively. Here are 8 fully solved varieties.
      </p>

      <h3>Problem 1: Finding Equilibrium</h3>
      <p>
        <strong>Q:</strong> Given Demand: Qd = 500 - 10P and Supply: Qs = 100 + 10P. Find the equilibrium price and quantity.
        <br /><strong>Solution:</strong>
        <br />At equilibrium, Qd = Qs.
        <br />500 - 10P = 100 + 10P
        <br />400 = 20P
        <br />P = 20
        <br />Substitute P back into either equation to find Q:
        <br />Q = 500 - 10(20) = 500 - 200 = 300.
        <br /><strong>Equilibrium Price = Rs. 20, Equilibrium Quantity = 300 units.</strong>
      </p>

      <h3>Problem 2: Point Elasticity of Demand</h3>
      <p>
        <strong>Q:</strong> For the demand function Qd = 100 - 2P, calculate the price elasticity of demand at P = 25.
        <br /><strong>Solution:</strong>
        <br />First, find Q at P = 25: Qd = 100 - 2(25) = 100 - 50 = 50.
        <br />Formula for point elasticity: e = (dQ/dP) * (P/Q)
        <br />The slope dQ/dP is the coefficient of P, which is -2.
        <br />e = (-2) * (25 / 50) = -2 * 0.5 = -1.
        <br /><strong>The elasticity is -1 (Unit Elastic).</strong>
      </p>

      <h3>Problem 3: Arc Elasticity</h3>
      <p>
        <strong>Q:</strong> When the price of a good increases from Rs. 10 to Rs. 12, the quantity demanded falls from 100 to 80 units. Calculate the Arc Price Elasticity.
        <br /><strong>Solution:</strong>
        <br />P1 = 10, P2 = 12. Q1 = 100, Q2 = 80.
        <br />Change in Q = 80 - 100 = -20. Average Q = (100 + 80)/2 = 90.
        <br />Change in P = 12 - 10 = 2. Average P = (10 + 12)/2 = 11.
        <br />Arc Elasticity = (Change in Q / Average Q) / (Change in P / Average P)
        <br />= (-20 / 90) / (2 / 11) = (-2/9) * (11/2) = -11/9 = -1.22
        <br /><strong>The Arc elasticity is -1.22.</strong>
      </p>

      <h3>Problem 4: Income Elasticity</h3>
      <p>
        <strong>Q:</strong> A consumer's income increases from Rs. 20,000 to Rs. 25,000. As a result, their demand for Good X increases from 40 units to 60 units. Calculate YED and identify the type of good.
        <br /><strong>Solution:</strong>
        <br />Change in Q = 60 - 40 = 20. Initial Q = 40. % Change in Q = (20/40)*100 = 50%.
        <br />Change in Income = 5000. Initial Income = 20000. % Change in Income = (5000/20000)*100 = 25%.
        <br />YED = (% Change in Q) / (% Change in Income) = 50% / 25% = 2.
        <br /><strong>Since YED = +2 (&gt;1), Good X is a Normal Good, specifically a Luxury.</strong>
      </p>

      <h3>Problem 5: Cross-Price Elasticity</h3>
      <p>
        <strong>Q:</strong> The price of Coffee rises by 10%. Consequently, the demand for Tea rises by 15%. Calculate XED and explain the relationship.
        <br /><strong>Solution:</strong>
        <br />XED = (% Change in Qd of Tea) / (% Change in Price of Coffee) = +15% / +10% = +1.5.
        <br /><strong>Since XED is positive, Tea and Coffee are substitute goods.</strong>
      </p>

      <h3>Problem 6: Shifts vs. Price Controls (Surplus Calculation)</h3>
      <p>
        <strong>Q:</strong> Let Qd = 200 - 5P and Qs = -50 + 5P. The government imposes a price floor of Rs. 30. Calculate the resulting market status (Surplus/Shortage amount).
        <br /><strong>Solution:</strong>
        <br />First, find free-market equilibrium to verify it's a binding floor.
        <br />200 - 5P = -50 + 5P -&gt; 250 = 10P -&gt; Pe = 25.
        <br />Since the Price Floor (30) is &gt; Pe (25), it is binding.
        <br />At P = 30:
        <br />Qd = 200 - 5(30) = 200 - 150 = 50 units.
        <br />Qs = -50 + 5(30) = -50 + 150 = 100 units.
        <br />Qs &gt; Qd, so there is a Surplus.
        <br />Surplus = Qs - Qd = 100 - 50 = 50 units.
        <br /><strong>There is a market surplus of 50 units.</strong>
      </p>

      <h3>Problem 7: Specific Tax Analysis</h3>
      <p>
        <strong>Q:</strong> Let Qd = 100 - P and Qs = P. A per-unit tax of Rs. 10 is imposed on sellers. Find the new equilibrium quantity, price paid by buyers (Pb), and price received by sellers (Ps).
        <br /><strong>Solution:</strong>
        <br />Initial equilibrium: 100 - P = P -&gt; 2P = 100 -&gt; P = 50, Q = 50.
        <br />With a tax t=10 on sellers, the supply curve shifts vertically upwards by 10.
        <br />New supply curve: P = Qs + 10. Or Qs = P - 10.
        <br />Equate Qd to new Qs: 100 - P = P - 10
        <br />110 = 2P -&gt; P = 55. This is the new market price paid by buyers (Pb = 55).
        <br />Price received by sellers = Pb - Tax = 55 - 10 = 45 (Ps = 45).
        <br />New Quantity = 100 - 55 = 45 units.
        <br /><strong>New Q = 45, Buyers pay 55, Sellers receive 45. (Burden is shared equally since elasticities are equal).</strong>
      </p>

      <h3>Problem 8: Calculating Deadweight Loss</h3>
      <p>
        <strong>Q:</strong> Using the data from Problem 7, calculate the Deadweight Loss (DWL) created by the tax.
        <br /><strong>Solution:</strong>
        <br />DWL is the area of the triangle formed by the tax wedge between the old Q and new Q.
        <br />DWL = 0.5 * Base * Height
        <br />Base = Tax wedge = (Pb - Ps) = 55 - 45 = 10.
        <br />Height = Change in Quantity = (Old Q - New Q) = 50 - 45 = 5.
        <br />DWL = 0.5 * 10 * 5 = 25.
        <br /><strong>The Deadweight Loss is Rs. 25.</strong>
      </p>

      <h2>DU Exam Strategy for Unit 2</h2>
      <h3>1. Mastering the 10-Mark Essay Question</h3>
      <p>
        Unit 2 often features long-form theoretical questions. A classic example is: <em>"Explain the incidence of taxation. Show how the burden of a tax is distributed between buyers and sellers under different elasticity scenarios."</em> (10 marks).
      </p>
      <p>
        <strong>How to structure your answer for maximum marks:</strong>
        <ul>
          <li><strong>Introduction (2 marks):</strong> Define tax incidence and state the golden rule: the burden falls heavier on the relatively inelastic side.</li>
          <li><strong>Scenario 1: Inelastic Demand, Elastic Supply (3 marks):</strong> Draw a steep demand curve and a flat supply curve. Shift supply by the tax. Show clearly on the diagram that the price consumers pay rises sharply, while the price producers receive falls only slightly. Conclude: Consumers bear the brunt. Example: Cigarette taxes.</li>
          <li><strong>Scenario 2: Elastic Demand, Inelastic Supply (3 marks):</strong> Draw a flat demand curve and a steep supply curve. Show that the price consumers pay barely rises, while the price producers receive falls sharply. Conclude: Producers bear the brunt. Example: Taxes on luxury yachts.</li>
          <li><strong>Conclusion (2 marks):</strong> Summarize that DWL also depends on elasticity. A quick summary sentence wraps it up nicely. Neat, labeled graphs are non-negotiable. Use different colors or shading for consumer vs. producer burden.</li>
        </ul>
      </p>

      <h3>2. Common Numerical Errors to Avoid</h3>
      <ul>
        <li><strong>Forgetting the minus sign in point elasticity:</strong> While we often talk about the absolute value, mathematically, the point elasticity of a downward-sloping demand curve is negative. If the question asks for the exact elasticity, state the negative value, and then explain it.</li>
        <li><strong>Confusing Arc vs. Point:</strong> If you are given two points (e.g., P falls from 10 to 8), DO NOT use the point elasticity formula just taking P=10. You must use the midpoint/arc formula unless explicitly told otherwise.</li>
        <li><strong>Graphing axes wrong:</strong> In economics, Price (the independent variable mathematically for demand) is ALWAYS on the vertical Y-axis, and Quantity on the horizontal X-axis. This inverse convention was established by Alfred Marshall. Do not flip them!</li>
        <li><strong>Price Ceiling vs Floor Confusion:</strong> Ceilings are only binding if they are set BELOW equilibrium. Floors are binding only if set ABOVE equilibrium. A price ceiling set at Rs. 100 when equilibrium is Rs. 50 has zero effect. Watch out for trick questions!</li>
      </ul>

      <p>
        <strong>Final Word:</strong> Unit 2 builds the mechanical engine of your economic intuition. Don't just memorize the shifts; understand the logic behind why consumers and producers react the way they do. Practice drawing the graphs until they become second nature, and solve as many past year papers as you can. Good luck with your Semester 1 preparations!
      </p>
    </div>
  );
}

